import React, { memo } from 'react';
import { Headline6, Paragraph4 } from '@nexus/lib-react/dist/core/Typography';
import { useI18n } from '../../../../i18n';
import S from './style/Dac7TabContent';
import V from './utils/validation';
import { SellerBusinessDac7 } from '../../../../../typings/dac7';
import Dac7Input from './components/Dac7Input';
import Dac7TaxDetails from './components/Dac7TaxDetails';
import Dac7PermanentEstablishment from './components/Dac7PermanentEstablishment';
import Dac7FinancialDetails from './components/Dac7FinancialDetails';
import Dac7ZipCodeField from './components/Dac7ZipCodeField';
import Dac7Dropdown from './components/Dac7Dropdown';
import countries from './utils/countriesList';

interface Props {
  data: Readonly<SellerBusinessDac7>;
  isDesktop: boolean;
}

const DAC7TabContentBusiness = ({ data, isDesktop }: Props) => {
  const i18n = useI18n();
  const isDisabled = !!data.submittedAt;

  return (
    <>
      <S.Dac7TabContentWrapper>
        <S.Dac7Fieldset className="dac7-personal-details">
          <legend>
            <Headline6 mb={12}>{i18n('dlv_dac7_businessDetails')}</Headline6>
          </legend>
          <S.Dac7FormField>
            <Dac7Input
              name="dac7_legalName"
              label={i18n('dlv_dac7_legalName')}
              pattern={V.LEGAL_NAME_PATTERN}
              required
              value={data.legalName}
              disabled={!!data.legalName}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7Dropdown
              sourceItems={countries}
              initialSelectedIds={[data.address?.country]}
              name="dac7_address.country"
              label={i18n('dlv_dac7_country')}
              required
              requiredMessage={i18n('dlv_dac7_address.countryRequired')}
              placeholder={i18n('dlv_dac7_countryPlaceholder')}
              disabled={!!data.address?.country}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7Input
              name="dac7_address.street"
              label={i18n('dlv_dac7_street')}
              pattern={V.getMinCharsPattern(2)}
              required
              value={data.address?.street || ''}
              disabled={!!data.address?.street}
            />
          </S.Dac7FormField>
          <S.Dac7HouseNumberApartmentContainer>
            <S.Dac7FormField>
              <Dac7Input
                name="dac7_address.houseNumber"
                label={i18n('dlv_dac7_houseNumber')}
                pattern={V.getMinCharsPattern(1)}
                required
                value={data.address?.houseNumber || ''}
                disabled={!!data.address?.houseNumber}
              />
            </S.Dac7FormField>
            <S.Dac7FormField>
              <Dac7Input
                name="dac7_address.apartmentNumber"
                label={i18n('dlv_dac7_apartmentNumber')}
                value={data.address?.apartmentNumber || ''}
                disabled={!!data.address?.apartmentNumber || !!data.address?.street}
              />
            </S.Dac7FormField>
          </S.Dac7HouseNumberApartmentContainer>
          <S.Dac7FormField>
            <Dac7Input
              name="dac7_address.city"
              label={i18n('dlv_dac7_city')}
              pattern={V.getMinCharsPattern(2)}
              required
              value={data.address?.city || ''}
              disabled={!!data.address?.city}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7ZipCodeField
              name="dac7_address.zipCode"
              label={i18n('dlv_dac7_zipCode')}
              required
              value={data.address?.zipCode || ''}
              disabled={!!data.address?.zipCode}
            />
          </S.Dac7FormField>
          <Dac7PermanentEstablishment
            permanentEstablishmentCountry={data.permanentEstablishmentCountry}
          />
        </S.Dac7Fieldset>
        <S.StyledRequiredFieldsText>
          {i18n(`dlv_dac7_required_fields_description`)}
        </S.StyledRequiredFieldsText>
      </S.Dac7TabContentWrapper>
      <S.StyledInvisibleDivider />
      <S.Dac7TabContentWrapper>
        <legend>
          <Headline6 mb={12} ml={isDesktop ? 0 : 12} mr={isDesktop ? 0 : 12}>
            {i18n('dlv_dac7_taxDetails')}
          </Headline6>
        </legend>
        {isDisabled ? null : (
          <Paragraph4 mt={8} mb={24} ml={isDesktop ? 0 : 12} mr={isDesktop ? 0 : 12}>
            {i18n('dlv_dac7_business_taxDetails_description')}
          </Paragraph4>
        )}
        <Dac7TaxDetails taxDetails={data.taxDetails} />
      </S.Dac7TabContentWrapper>
      <S.StyledInvisibleDivider />
      <S.Dac7TabContentWrapper>
        <S.Dac7Fieldset className="dac7-financial-details">
          <legend>
            <Headline6 mb={12}>{i18n('dlv_dac7_financialDetails')}</Headline6>
          </legend>
          <Dac7FinancialDetails financialDetails={data.financialDetails} />
        </S.Dac7Fieldset>
      </S.Dac7TabContentWrapper>
    </>
  );
};

export default memo(DAC7TabContentBusiness);
